import { Metric } from "@linkfluence/models/metric";
import { Platform } from "@linkfluence/models/platform";
import { InfluencerLens } from "./generic";
import { OfflineInfluencer, OfflineInfluencerLens } from "./offline-influencer";
import * as Social from "./social-influencer";
import { WebInfluencer, WebInfluencerLens } from "./web-influencer";

export interface SocialAccount {
  website: string;
}

export interface Geo {
  country: string;
  location: Record<string, unknown>;
  cid: string;
  admin1: string;
}

export interface Audience {
  meanTraffic: number;
  lastMonthTraffic: number;
  potentialImpression: number;
  country: string;
  trafficShares: {
    referrals: number;
    direct: number;
    paidReferrals: number;
    mail: number;
    social: number;
    search: number;
  };
  countryShares: { country: string; trafficShare: number }[];
}

const influencerPlatforms: Partial<
  Record<Platform, typeof InfluencerLens<any>>
> = {
  // SOCIAL
  [Platform.Twitter]: Social.TwitterLens,
  [Platform.Facebook]: Social.FacebookLens,
  [Platform.Vkontakte]: Social.VkontakteLens,
  [Platform.Instagram]: Social.InstagramLens,
  [Platform.Dailymotion]: Social.DailymotionLens,
  [Platform.Youtube]: Social.YoutubeLens,
  [Platform.Twitch]: Social.TwitchLens,
  [Platform.Pinterest]: Social.PinterestLens,
  [Platform.Reddit]: Social.RedditLens,
  [Platform.Linkedin]: Social.LinkedinLens,
  [Platform.Sinaweibo]: Social.SinaweiboLens,
  [Platform.Wechat]: Social.WechatLens,
  [Platform.Youku]: Social.YoukuLens,
  [Platform.LittleRedBook]: Social.LittleredbookLens,
  [Platform.Bilibili]: Social.BilibiliLens,
  [Platform.Douyin]: Social.DouyinLens,
  [Platform.Tiktok]: Social.TiktokLens,
  // WEB
  [Platform.Website]: WebInfluencerLens,
  [Platform.Media]: WebInfluencerLens,
  [Platform.Blog]: WebInfluencerLens,
  [Platform.Forum]: WebInfluencerLens,
  [Platform.Review]: WebInfluencerLens,
  // OFFLINE
  [Platform.Radio]: OfflineInfluencerLens,
  [Platform.Television]: OfflineInfluencerLens,
  [Platform.Press]: OfflineInfluencerLens,
};

export type InfluencerPlatforms = {
  // SOCIAL
  [Platform.Twitter]: Social.TwitterInfluencer;
  [Platform.Facebook]: Social.FacebookInfluencer;
  [Platform.Vkontakte]: Social.VkontakteInfluencer;
  [Platform.Instagram]: Social.InstagramInfluencer;
  [Platform.Dailymotion]: Social.DailymotionInfluencer;
  [Platform.Youtube]: Social.YoutubeInfluencer;
  [Platform.Twitch]: Social.TwitchInfluencer;
  [Platform.Pinterest]: Social.PinterestInfluencer;
  [Platform.Reddit]: Social.RedditInfluencer;
  [Platform.Linkedin]: Social.LinkedinInfluencer;
  [Platform.Sinaweibo]: Social.SinaweiboInfluencer;
  [Platform.Wechat]: Social.WechatInfluencer;
  [Platform.Youku]: Social.YoukuInfluencer;
  [Platform.LittleRedBook]: Social.LittleredbookInfluencer;
  [Platform.Bilibili]: Social.BilibiliInfluencer;
  [Platform.Douyin]: Social.DouyinInfluencer;
  [Platform.Tiktok]: Social.TiktokInfluencer;
  // WEB
  [Platform.Website]: WebInfluencer;
  [Platform.Media]: WebInfluencer;
  [Platform.Blog]: WebInfluencer;
  [Platform.Forum]: WebInfluencer;
  [Platform.Review]: WebInfluencer;
  // OFFLINE
  [Platform.Radio]: OfflineInfluencer;
  [Platform.Television]: OfflineInfluencer;
  [Platform.Press]: OfflineInfluencer;
};

type MetricsCounts = Partial<Record<Metric, number>>;

export type InfluencerAccountMetric =
  | {
      key: "COUNTRY";
      value: string;
    }
  | {
      key: "FANS";
      value: number;
    }
  | {
      key: "FOLLOWINGS";
      value: number;
    }
  | {
      key: "FOLLOWERS";
      value: number;
    }
  | {
      key: "POSTS";
      value: number;
    }
  | {
      key: "PUBLICATIONS";
      value: number;
    }
  | {
      key: "TWEETS";
      value: number;
    }
  | {
      key: "VIDEOS";
      value: number;
    }
  | {
      key: "SUBSCRIBERS";
      value: number;
    }
  | {
      key: "AVERAGE_TRAFFIC";
      value: number;
    }
  | {
      key: "PAGE_RANK";
      value: number;
    };

export type Influencer =
  | Social.SocialInfluencer
  | WebInfluencer
  | OfflineInfluencer;

export interface FullInfluencer {
  user: Influencer;
  metrics: any; // @TODO
}

export interface SearchableInfluencer {
  user: Influencer;
  key: string;
  counts: MetricsCounts;
  raw_doc_count?: number;
  aggs?: any; // @TODO
}

export interface GenericInfluencer<P extends Platform> {
  idStr?: string;
  uid: string;
  platform: P;
  tags: string[];
  name?: string;
}

export class InfluencerLensFactory<I extends Influencer> {
  influencer: I;
  static create<I extends Influencer>(influencer: I): InfluencerLens<I> {
    if (influencerPlatforms[influencer.platform]) {
      return new influencerPlatforms[influencer.platform](influencer);
    }
    return new InfluencerLens(influencer);
  }
}
